import React from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme } from 'react-jss';
import { withSpacing, clsx } from '@wds/utils';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var useStyles = createUseStyles({
  wrenchBox: function wrenchBox(_ref) {
    var display = _ref.display,
        flexProperties = _ref.flexProperties;
    var displayProperty = display && {
      display: display
    };
    var flexJustify = flexProperties.flexJustify && {
      justifyContent: flexProperties.flexJustify
    };
    var flexDirection = flexProperties.flexDirection && {
      flexDirection: flexProperties.flexDirection
    };
    var flexAlign = flexProperties.flexAlign && {
      alignItems: flexProperties.flexAlign
    };
    var flexWrap = flexProperties.flexWrap && {
      flexWrap: 'wrap'
    };
    var flexItemGrow = flexProperties.flexItemGrow && {
      flexGrow: 1
    };
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, displayProperty), flexJustify), flexDirection), flexAlign), flexWrap), flexItemGrow);
  }
});

var _excluded = ["element", "children", "containerRef", "display", "flexDirection", "flexJustify", "flexAlign", "flexWrap", "flexItemGrow", "className"];
var displayOptions = Object.freeze(['block', 'inline-block', 'flex', 'inline-flex', 'inline']);
var flexDirectionOptions = Object.freeze(['row', 'column']);
var flexJustifyOptions = Object.freeze(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly', 'stretch']);
var flexAlignOptions = Object.freeze(['start', 'end', 'center', 'space-between', 'space-around', 'flex-start', 'flex-end', 'stretch', 'baseline']);

var Box = function Box(_ref) {
  var element = _ref.element,
      children = _ref.children,
      containerRef = _ref.containerRef,
      display = _ref.display,
      flexDirection = _ref.flexDirection,
      flexJustify = _ref.flexJustify,
      flexAlign = _ref.flexAlign,
      flexWrap = _ref.flexWrap,
      flexItemGrow = _ref.flexItemGrow,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded);

  var flexProperties = {
    flexDirection: flexDirection,
    flexJustify: flexJustify,
    flexAlign: flexAlign,
    flexWrap: flexWrap,
    flexItemGrow: flexItemGrow
  };
  var theme = useTheme();
  var classes = useStyles({
    display: display,
    flexProperties: flexProperties,
    theme: theme
  });
  var Element = element;
  var classNames = clsx(classes.wrenchBox, className);
  return /*#__PURE__*/React.createElement(Element, Object.assign({
    className: classNames,
    ref: containerRef
  }, rest), children);
};

Box.displayName = "Box";
Box.defaultProps = {
  element: 'div',
  containerRef: undefined,
  display: undefined,
  flexDirection: undefined,
  flexJustify: undefined,
  flexAlign: undefined,
  flexWrap: undefined,
  flexItemGrow: undefined,
  className: undefined
};
Box.propTypes = {
  element: PropTypes.string,
  children: PropTypes.node.isRequired,
  containerRef: PropTypes.func,

  /**
   * CSS display property
   */
  display: PropTypes.oneOf(displayOptions),

  /**
   * CSS flex-direction
   */
  flexDirection: PropTypes.oneOf(flexDirectionOptions),

  /**
   * CSS justify-content property
   */
  flexJustify: PropTypes.oneOf(flexJustifyOptions),

  /**
   * CSS align-items property
   */
  flexAlign: PropTypes.oneOf(flexAlignOptions),

  /**
   * Will wrap child items if needed, uses the flex-flow property
   */
  flexWrap: PropTypes.bool,

  /**
   * CSS flex-grow property
   */
  flexItemGrow: PropTypes.bool,

  /**
   * An internal prop that is being used by withSpacing HoC
   */
  className: PropTypes.string
};
Box.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Box",
  "props": {
    "element": {
      "defaultValue": {
        "value": "'div'",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "containerRef": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "display": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "displayOptions"
      },
      "required": false,
      "description": "CSS display property"
    },
    "flexDirection": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "flexDirectionOptions"
      },
      "required": false,
      "description": "CSS flex-direction"
    },
    "flexJustify": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "flexJustifyOptions"
      },
      "required": false,
      "description": "CSS justify-content property"
    },
    "flexAlign": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "flexAlignOptions"
      },
      "required": false,
      "description": "CSS align-items property"
    },
    "flexWrap": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Will wrap child items if needed, uses the flex-flow property"
    },
    "flexItemGrow": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "CSS flex-grow property"
    },
    "className": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "An internal prop that is being used by withSpacing HoC"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": ""
    }
  }
};
var Box$1 = withSpacing(Box);

export { Box$1 as Box };
//# sourceMappingURL=index.es.js.map
